import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  Plus, FileText, CheckCircle2, AlertCircle, Archive, ArchiveRestore, X, Loader2,
  Search, KeyRound, UserCog, Settings2, Trash2,
} from "lucide-react";
import Modal from "@/components/Modal";

const inputCls =
  "mt-1.5 w-full bg-[#0D0E15] border border-slate-800 rounded-lg px-3 py-2 text-sm text-slate-100 outline-none focus:border-indigo-500/60 transition-colors";

export default function Clients() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const isExec = user?.role === "executive";
  const [clients, setClients] = useState([]);
  const [execs, setExecs] = useState([]);
  const [showArchived, setShowArchived] = useState(false);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [manage, setManage] = useState(null); // client being managed by admin
  const [form, setForm] = useState({
    name: "", email: "", target_summary: "", executive_id: "",
    login_password: "", intake_text: "", resume_text: "",
  });

  const load = () => api.get(`/clients?include_archived=${showArchived}`).then((r) => setClients(r.data));

  useEffect(() => { load(); }, [showArchived]);
  useEffect(() => { if (isAdmin) api.get("/users").then((r) => setExecs(r.data)); }, [isAdmin]);

  const execName = (id) => execs.find((e) => e.id === id)?.name || "Unassigned";
  const clientExecIds = (c) => {
    const ids = [...(c.executive_ids || [])];
    if (c.executive_id && !ids.includes(c.executive_id)) ids.unshift(c.executive_id);
    return ids;
  };

  const deleteClient = async (c) => {
    if (!window.confirm(`Delete ${c.name}? This removes their profile, login and all logged evaluations. This cannot be undone.`)) return;
    try {
      await api.delete(`/clients/${c.id}`);
      toast.success("Client deleted");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const create = async () => {
    if (!form.name.trim()) { toast.error("Client name is required"); return; }
    if (isAdmin && !form.executive_id) { toast.error("Assign an executive"); return; }
    if (isExec && (!form.email.trim() || !form.login_password.trim())) {
      toast.error("Client login email and password are required"); return;
    }
    if (form.login_password && !form.email) { toast.error("Email is required to create a client login"); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/clients", form);
      if (data?.linked) {
        toast.success(data.message || "This client already exists — added to your profile");
      } else {
        toast.success("Client created & assigned");
      }
      setOpen(false);
      setForm({ name: "", email: "", target_summary: "", executive_id: "", login_password: "", intake_text: "", resume_text: "" });
      load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  const toggleArchive = async (c) => {
    await api.post(`/clients/${c.id}/archive`);
    toast.success(c.archived ? "Client restored" : "Client archived");
    load();
  };

  const filtered = clients.filter((c) => c.name.toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="space-y-6 animate-fade-up">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold text-slate-100 tracking-tight">Clients</h1>
          <p className="text-slate-400 text-sm mt-1">
            {isAdmin ? "Create clients, assign them to executives, and manage client logins." : "Create clients and manage the ones assigned to you."}
          </p>
        </div>
        {(isAdmin || isExec) && (
          <button data-testid="new-client-btn" onClick={() => setOpen(true)} className="flex items-center gap-2 bg-indigo-500 hover:bg-indigo-600 text-white text-sm font-semibold px-4 py-2.5 rounded-lg transition-colors">
            <Plus className="w-4 h-4" /> New client
          </button>
        )}
      </div>

      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[220px] max-w-sm">
          <Search className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
          <input data-testid="client-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search clients…"
            className="w-full bg-[#12151F] border border-slate-800 rounded-lg pl-9 pr-3 py-2 text-sm text-slate-200 outline-none focus:border-indigo-500/60 transition-colors" />
        </div>
        <label className="flex items-center gap-2 text-sm text-slate-400 cursor-pointer">
          <input type="checkbox" data-testid="toggle-archived" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} className="accent-indigo-500" />
          Show archived
        </label>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {filtered.map((c) => (
          <div key={c.id} data-testid={`client-card-${c.id}`} className={`bg-[#12151F] border rounded-xl p-5 shadow-lg shadow-black/40 hover:-translate-y-0.5 transition-transform duration-150 ${c.archived ? "border-slate-800/50 opacity-70" : "border-slate-800/80 hover:border-slate-700/80"}`}>
            <div className="flex items-start justify-between">
              <Link to={`/clients/${c.id}`} className="flex items-center gap-3 min-w-0">
                <div className="w-11 h-11 rounded-full bg-indigo-500/15 border border-indigo-500/30 flex items-center justify-center text-indigo-300 font-bold">{c.name[0]}</div>
                <div className="min-w-0">
                  <div className="font-semibold text-slate-100 truncate">{c.name}</div>
                  <div className="text-xs text-slate-500 truncate">{c.email || "no email"}</div>
                </div>
              </Link>
              <div className="flex items-center gap-1">
                {isAdmin && (
                  <button data-testid={`manage-${c.id}`} onClick={() => setManage(c)} title="Assign / login" className="text-slate-500 hover:text-indigo-300 transition-colors p-1">
                    <Settings2 className="w-4 h-4" />
                  </button>
                )}
                {isAdmin && (
                  <button data-testid={`delete-client-${c.id}`} onClick={() => deleteClient(c)} title="Delete client" className="text-slate-500 hover:text-rose-400 transition-colors p-1">
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
                <button data-testid={`archive-${c.id}`} onClick={() => toggleArchive(c)} title={c.archived ? "Restore" : "Archive"} className="text-slate-500 hover:text-slate-200 transition-colors p-1">
                  {c.archived ? <ArchiveRestore className="w-4 h-4" /> : <Archive className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <p className="text-xs text-slate-400 mt-3 line-clamp-2 min-h-[32px]">{c.target_summary || "No target summary yet."}</p>

            <div className="flex items-center gap-2 mt-3 flex-wrap text-[11px]">
              {isAdmin && clientExecIds(c).map((eid) => (
                <span key={eid} className="flex items-center gap-1 text-slate-400 bg-slate-800/60 border border-slate-700/50 px-2 py-0.5 rounded-full">
                  <UserCog className="w-3 h-3" /> {execName(eid)}
                </span>
              ))}
              <span className={`flex items-center gap-1 px-2 py-0.5 rounded-full border ${c.has_login ? "text-emerald-400 border-emerald-500/30 bg-emerald-500/10" : "text-slate-500 border-slate-700/50 bg-slate-800/40"}`}>
                <KeyRound className="w-3 h-3" /> {c.has_login ? "Has login" : "No login"}
              </span>
            </div>

            <div className="flex items-center gap-3 mt-3 text-[11px]">
              <span className={`flex items-center gap-1 ${c.resume_text ? "text-emerald-400" : "text-slate-600"}`}><FileText className="w-3.5 h-3.5" /> Resume</span>
              <span className={`flex items-center gap-1 ${c.intake_text ? "text-emerald-400" : "text-slate-600"}`}><FileText className="w-3.5 h-3.5" /> Intake</span>
              <span className={`flex items-center gap-1 ml-auto ${c.profile ? "text-emerald-400" : "text-amber-400"}`}>
                {c.profile ? <CheckCircle2 className="w-3.5 h-3.5" /> : <AlertCircle className="w-3.5 h-3.5" />}
                {c.profile ? "Profile ready" : "No profile"}
              </span>
            </div>
            <Link to={`/clients/${c.id}`} data-testid={`open-client-${c.id}`} className="mt-4 block text-center text-sm text-indigo-300 hover:text-indigo-200 bg-indigo-500/10 hover:bg-indigo-500/15 border border-indigo-500/25 rounded-lg py-2 transition-colors">
              Open profile
            </Link>
          </div>
        ))}
        {filtered.length === 0 && <div className="col-span-full text-center text-slate-500 py-16 text-sm">No clients found.</div>}
      </div>

      {/* Create modal (admin) */}
      {open && (
        <Modal onClose={() => setOpen(false)}>
          <div className="bg-[#141724] border border-slate-800 rounded-2xl w-full max-w-lg shadow-2xl">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-800/60">
              <h3 className="font-heading font-semibold text-slate-100">New client</h3>
              <button data-testid="close-modal" onClick={() => setOpen(false)} className="text-slate-500 hover:text-slate-200"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-6 space-y-4 max-h-[70vh] overflow-y-auto">
              <div>
                <label className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Client name *</label>
                <input data-testid="field-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Jordan Patel" className={inputCls} />
              </div>
              {isAdmin && (
                <div>
                  <label className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Assign to executive *</label>
                  <select data-testid="field-executive" value={form.executive_id} onChange={(e) => setForm({ ...form, executive_id: e.target.value })} className={inputCls}>
                    <option value="">Select an executive…</option>
                    {execs.map((e) => <option key={e.id} value={e.id}>{e.name} ({e.email})</option>)}
                  </select>
                </div>
              )}
              <div>
                <label className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Email (used for client login){isExec ? " *" : ""}</label>
                <input data-testid="field-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="jordan@example.com" className={inputCls} />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Client login password{isExec ? " *" : " (optional)"}</label>
                <input data-testid="field-login-password" value={form.login_password} onChange={(e) => setForm({ ...form, login_password: e.target.value })} placeholder="Set a password to give the client portal access" className={inputCls} />
                {isExec && <p className="text-[11px] text-slate-500 mt-1.5">Your admin can view these login details. If this client already exists (same email), they'll simply be added to your profile so you can both work on them.</p>}
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Short target summary</label>
                <input data-testid="field-target_summary" value={form.target_summary} onChange={(e) => setForm({ ...form, target_summary: e.target.value })} placeholder="Senior Backend Engineer, needs sponsorship" className={inputCls} />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Intake form (paste)</label>
                <textarea data-testid="field-intake" rows={3} value={form.intake_text} onChange={(e) => setForm({ ...form, intake_text: e.target.value })} className={inputCls} />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Resume text (paste, optional)</label>
                <textarea data-testid="field-resume" rows={3} value={form.resume_text} onChange={(e) => setForm({ ...form, resume_text: e.target.value })} className={inputCls} />
              </div>
            </div>
            <div className="flex justify-end gap-3 px-6 py-4 border-t border-slate-800/60">
              <button onClick={() => setOpen(false)} className="text-sm text-slate-400 hover:text-slate-200 px-4 py-2">Cancel</button>
              <button data-testid="save-client" onClick={create} disabled={saving} className="flex items-center gap-2 bg-indigo-500 hover:bg-indigo-600 text-white text-sm font-semibold px-4 py-2 rounded-lg transition-colors disabled:opacity-60">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Create client"}
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* Manage modal (admin): assign executive + set login */}
      {manage && (
        <ManageModal client={manage} execs={execs} onClose={() => setManage(null)} onDone={() => { setManage(null); load(); }} />
      )}
    </div>
  );
}

function ManageModal({ client, execs, onClose, onDone }) {
  const initIds = (() => {
    const ids = [...(client.executive_ids || [])];
    if (client.executive_id && !ids.includes(client.executive_id)) ids.unshift(client.executive_id);
    return ids;
  })();
  const [execIds, setExecIds] = useState(initIds);
  const [email, setEmail] = useState(client.email || "");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const toggleExec = (id) => setExecIds((cur) => cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]);

  const saveAssign = async () => {
    setBusy(true);
    try {
      await api.post(`/clients/${client.id}/assign`, { executive_ids: execIds });
      toast.success(execIds.length === 0 ? "Client unassigned (admin-only until reassigned)" : "Executives updated");
      onDone();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  const saveLogin = async () => {
    if (!email || !password) { toast.error("Email and password required"); return; }
    setBusy(true);
    try {
      await api.post(`/clients/${client.id}/login`, { email, password });
      toast.success("Client login set");
      onDone();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <Modal onClose={onClose}>
      <div className="bg-[#141724] border border-slate-800 rounded-2xl w-full max-w-md shadow-2xl">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-800/60">
          <h3 className="font-heading font-semibold text-slate-100">Manage {client.name}</h3>
          <button data-testid="close-manage" onClick={onClose} className="text-slate-500 hover:text-slate-200"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-6 space-y-5">
          <div>
            <label className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Assigned executives (a client can have several)</label>
            <div className="mt-2 space-y-1.5 max-h-44 overflow-y-auto">
              {execs.map((e) => {
                const on = execIds.includes(e.id);
                return (
                  <button key={e.id} type="button" data-testid={`assign-exec-${e.id}`} onClick={() => toggleExec(e.id)}
                    className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg border text-sm transition-colors ${on ? "bg-indigo-500/20 text-indigo-200 border-indigo-500/40" : "bg-[#0D0E15] text-slate-300 border-slate-800 hover:border-slate-600"}`}>
                    <span className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 ${on ? "bg-indigo-500 border-indigo-500" : "border-slate-600"}`}>
                      {on && <CheckCircle2 className="w-3 h-3 text-white" />}
                    </span>
                    <span className="truncate">{e.name} <span className="text-slate-500 text-xs">· {e.email}</span></span>
                  </button>
                );
              })}
            </div>
            <button data-testid="save-assign" onClick={saveAssign} disabled={busy} className="mt-2 w-full bg-indigo-500 hover:bg-indigo-600 text-white text-sm font-semibold py-2 rounded-lg transition-colors disabled:opacity-60">Save executives</button>
          </div>
          <div className="border-t border-slate-800/60 pt-4">
            <label className="text-xs uppercase tracking-wider text-slate-500 font-semibold flex items-center gap-1.5"><KeyRound className="w-3.5 h-3.5" /> Client login</label>
            <input data-testid="login-email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="client@email.com" className={inputCls} />
            <input data-testid="login-password-set" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="New password" className={inputCls} />
            <button data-testid="save-login" onClick={saveLogin} disabled={busy} className="mt-2 w-full bg-slate-800 hover:bg-slate-700 text-slate-100 text-sm font-semibold py-2 rounded-lg transition-colors disabled:opacity-60">
              {client.has_login ? "Update login" : "Create login"}
            </button>
            <p className="text-[11px] text-slate-500 mt-1.5">The client uses these to sign in and track their own applications.</p>
          </div>
        </div>
      </div>
    </Modal>
  );
}
